import { flexRender, Table as TableInstance } from '@tanstack/react-table'
import { cn } from '../../utils/cn'

import { SortUp } from '../../icons/sort-up'
import { SortDown } from '../../icons/sort-down'

interface ITableStyles {
  table?: string
  header?: string
  headerCell?: string
  tableRow?: {
    style?: string
    selected?: string
    unSelected?: string
  }
  tableCell?: string
}

interface ITableProps<T> {
  instance: TableInstance<T>
  tableStyles?: ITableStyles
}

const sortIconClass = 'h-2 w-2 text-blue-500 ml-2'

export function Table<T>({ instance, tableStyles }: ITableProps<T>) {
  if (!instance) {
    return <>No Table instance</>
  }
  return (
    <div>
      <table
        style={{ width: instance.getCenterTotalSize() }}
        className={cn(tableStyles?.table, {
          'max-w-5xl rounded-full': !tableStyles?.table
        })}>
        <thead>
          <tr
            className={cn(
              tableStyles?.header ||
                'h-14 border-l-4 border-solid border-l-slate-50 bg-slate-50'
            )}>
            {instance.getLeafHeaders().map(header => {
              const canSort = header.column.getCanSort()
              const sorted = header.column.getIsSorted()

              let ariaSort: 'ascending' | 'descending' | 'none' | undefined
              if (canSort) {
                if (sorted === 'asc') ariaSort = 'ascending'
                else if (sorted === 'desc') ariaSort = 'descending'
                else ariaSort = 'none'
              }

              return (
                <th
                  key={header.id}
                  scope="col"
                  aria-sort={ariaSort}
                  colSpan={header.colSpan}
                  className={cn(tableStyles?.headerCell || 'pl-4 align-middle')}
                  style={{ width: header.getSize() }}>
                  {!header.isPlaceholder && (
                    <div
                      className={cn(
                        'flex flex-row items-center justify-center',
                        canSort ? 'cursor-pointer select-none' : '',
                        tableStyles?.headerCell
                      )}>
                      {canSort ? (
                        // Sortable headers must be keyboard-operable, so the
                        // sort toggle is a real <button> (focusable + Enter/
                        // Space) rather than an onClick on a non-focusable div.
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="flex flex-row items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-1">
                          <span className="sr-only">Sort by </span>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: (
                              <SortUp
                                aria-hidden="true"
                                className={sortIconClass}
                              />
                            ),
                            desc: (
                              <SortDown
                                aria-hidden="true"
                                className={sortIconClass}
                              />
                            )
                          }[sorted as string] ?? null}
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )
                      )}
                    </div>
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {instance.getRowModel().rows.map(row => (
            <tr
              key={row.id}
              className={cn(
                {
                  [tableStyles?.tableRow?.selected as string]:
                    row.getIsSelected() && tableStyles?.tableRow?.selected,
                  [tableStyles?.tableRow?.unSelected as string]:
                    !row.getIsSelected() && tableStyles?.tableRow?.unSelected
                },
                tableStyles?.tableRow?.style
              )}>
              {row.getVisibleCells().map(cell => (
                <td
                  className="max-w-[11rem] pl-4 text-center align-middle"
                  key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
